import React, { useLayoutEffect, useReducer, useRef } from 'react';
import * as d3 from 'd3';
import produce from 'immer';
import useSound from 'use-sound';

import RectangularBoard from '../../assets/rectangular-board.svg';
import PinkBall from '../../assets/pink-ball.svg';
import PinkBallInverted from '../../assets/pink-ball-inverted.svg';
import RedBall from '../../assets/red-ball.svg';
import RedBallInverted from '../../assets/red-ball-inverted.svg';
import OrangeBall from '../../assets/orange-ball.svg';
import OrangeBallInverted from '../../assets/orange-ball-inverted.svg';
import YellowBall from '../../assets/yellow-ball.svg';
import YellowBallInverted from '../../assets/yellow-ball-inverted.svg';
import GreenBall from '../../assets/green-ball.svg';
import GreenBallInverted from '../../assets/green-ball-inverted.svg';
import LightGreenBall from '../../assets/light-green-ball.svg';
import LightGreenBallInverted from '../../assets/light-green-ball-inverted.svg';
import BlueBall from '../../assets/blue-ball.svg';
import BlueBallInverted from '../../assets/blue-ball-inverted.svg';
import DarkBlueBall from '../../assets/dark-blue-ball.svg';
import DarkBlueBallInverted from '../../assets/dark-blue-ball-inverted.svg';
import VioletBall from '../../assets/violet-ball.svg';
import VioletBallInverted from '../../assets/violet-ball-inverted.svg';
import PurpleBall from '../../assets/purple-ball.svg';
import PurpleBallInverted from '../../assets/purple-ball-inverted.svg';

const ROWS_DEF = [
  {
    x0: 16,
    xSpacing: 118,
    y0: 40,
    ball: PinkBall,
    invertedBall: PinkBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 240,
    ball: RedBall,
    invertedBall: RedBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 440,
    ball: OrangeBall,
    invertedBall: OrangeBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 636,
    ball: YellowBall,
    invertedBall: YellowBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 840,
    ball: GreenBall,
    invertedBall: GreenBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 1040,
    ball: LightGreenBall,
    invertedBall: LightGreenBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 1240,
    ball: DarkBlueBall,
    invertedBall: DarkBlueBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 1440,
    ball: BlueBall,
    invertedBall: BlueBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 1640,
    ball: VioletBall,
    invertedBall: VioletBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
  {
    x0: 16,
    xSpacing: 118,
    y0: 1830,
    ball: PurpleBall,
    invertedBall: PurpleBallInverted,
    items: Array.from(Array(10)).map(() => ['', false]),
  },
];

const ACTION_TYPE_POP_IT = 'ACTION_POP_IT';

const reducer = (state, action) => {
  if (action.type === ACTION_TYPE_POP_IT) {
    const { row, col } = action.payload;
    return produce(state, draft => {
      draft[row].items[col][1] = true;
    });
  }

  return state;
};

const DRAWING_WIDTH = 1200;
const DRAWING_HEIGHT = 2000;

const SimpleBoard = ({ bounds, interactive = true }) => {
  console.log({ bounds });

  const containerRef = useRef(null);
  const [state, dispatch] = useReducer(reducer, ROWS_DEF);
  const [playPopSound] = useSound('/pop_sound.mp3');

  useLayoutEffect(() => {
    const FINAL_WIDTH = (DRAWING_WIDTH * bounds.height) / DRAWING_HEIGHT;

    const ref0 = d3.select(containerRef.current).select('g');
    ref0.selectAll('*').remove();

    const ref = ref0.append('g');

    // ref.attr('width', FINAL_WIDTH).attr('height', FINAL_HEIGHT);

    ref.append('image').attr('xlink:href', RectangularBoard);

    state.forEach(({ ball, invertedBall, items, x0, y0, xSpacing }, row) => {
      items.forEach(([letter, isPopped], col) => {
        const group = ref.append('g').attr('transform', `translate(${x0 + xSpacing * col},${y0})`);

        if (interactive) {
          group.style('cursor', 'pointer').on('click', () => {
            // alert(`${row}, ${col}`);
            if (!isPopped) playPopSound();

            dispatch({ type: ACTION_TYPE_POP_IT, payload: { row, col } });
          });
        }

        group.append('image').attr('xlink:href', isPopped ? invertedBall : ball);

        group
          .append('text')
          .attr('x', 50)
          .attr('y', 55)
          .text(letter)
          .attr('font-family', 'Roboto')
          .attr('font-size', '60px')
          .attr('dy', '0.35em')
          .style('text-anchor', 'middle')
          .style('opacity', isPopped ? 0.8 : 1);
      });
    });

    // .attr('height', 400);

    const scale = FINAL_WIDTH / DRAWING_WIDTH;
    ref.attr('transform', `scale(${scale},${scale})`);

    return () => ref0.selectAll('*').remove();
  }, [state, playPopSound, bounds, interactive]);

  return (
    <svg width={(DRAWING_WIDTH * bounds.height) / DRAWING_HEIGHT} height={bounds.height} ref={containerRef}>
      <g />
    </svg>
  );
};

SimpleBoard.propTypes = {};

export default SimpleBoard;
